import { DatePipe } from '@angular/common';
import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { TwcValidators, TwcWizardComponent } from '@topcon/twc-angular';

import { Event, SubscriptionHandler } from '@mirage-projects/communication-library';
import {
    AuthorizationService,
    ImportWizardOptions,
    ImportWizardType,
    MagnetService,
    ReportService,
    TranslationService,
    UploadService,
    WizardSummaryData,
} from '@mirage-projects/cw-commons';
import {
    PcvizCRP1FileData,
    PcvizCRP1ScanProjectJobAdapter,
    PcvizJobAdapter,
    PcvizUploadConfig,
    ProjectType,
    Report,
} from '@mirage-projects/cw-models';
import { DbPcvizService, JobPcvizService } from '@mirage-projects/cw-pcviz-core';
import { CustomFormValidatorsService, FilesHelperService } from '@mirage-projects/cw-utils';

import { WizardPanelTypes } from '../../components/create-wizard/create-wizard-input-data/wizard-panel-types';
import { GisImporterConversionOptions } from '../../components/create-wizard/gis-import-options/gis-import-options.component';
import { JobsInitializationService } from '../../components/create-wizard/jobs-initialization.service';
import { PointcloudConversionOptions } from '../../components/create-wizard/pointcloud-import-options/pointcloud-import-options.component';
import { UavImportOptions } from '../../components/create-wizard/uav-import-options/uav-import-options';
import { WorkspaceService } from '../../components/create-wizard/workspace/workspace.service';
import { ObjectBrowserSelectionService } from '../../components/object-browser/selection.service';
import { KeyValue } from '../../models/key-value';
import { WizardFormatDefinition } from '../../models/wizard-format-option';
import { CRP1ImportSdService } from '../crp1-import-sd/crp1-import-sd.service';
import { WorkflowWizardService } from '../workflow/workflow-wizard.service';

export type LayerSelectionMode = 'none' | 'new' | 'existing';

/** Service to manage the logic of the creation wizard service */
@Injectable()
export class UploadWizardService implements OnDestroy {
    readonly regexpProjectNameSite =
        /^[^:*?!"#$%&',.;<=>@^`|~¥/\\ \u3000-\u303F \u3040-\u309F \u30A0-\u30FF \uFF00-\uFFEF \u4E00-\u9FAF]*$/;

    readonly regexpProjectNameSiteVal = Validators.pattern(this.regexpProjectNameSite);

    wizardTitle = 'UPLOAD.TITLE';
    wizardSubtitle = '';
    wizardParams: any;
    formatSelection: WizardFormatDefinition | undefined;
    formatsDefinition = new BehaviorSubject<WizardFormatDefinition[]>([]);
    importType: ImportWizardType | undefined;
    selectInitialJob = false;
    crsValue: KeyValue = { key: '', value: '' };
    stepChanged!: EventEmitter<TwcWizardComponent>;
    companyReport: Report | null = null;
    projectType: ProjectType | null = null;
    errorFormCreateProject = '';
    isWorkspaceValid = false;
    nameProjectMaxLength: number | null = null;
    today = new Date();
    projectForm: FormGroup = this.formBuilder.group({
        projectName: [null, [Validators.required]],
        projectCaptureDate: [null, [TwcValidators.date, TwcValidators.maxDate(this.today)]],
    });
    layerForm: FormGroup = this.formBuilder.group(
        {
            name: [null, [Validators.required]],
            description: [null],
            hasProject: [false],
            projectName: [null],
            projectDescription: [null],
            projectCaptureDate: [null, [TwcValidators.date, TwcValidators.maxDate(this.today)]],
        },
        {
            validators: [this.formValidatorsService.conditionalRequiredValidator('projectName', (form) => form.value['hasProject'])],
        }
    );
    layerSelectionMode: LayerSelectionMode = 'none';
    startedUpload = false;
    wizard!: TwcWizardComponent;
    extraPanel: { panelName: WizardPanelTypes | undefined } = {
        panelName: undefined,
    };
    summary: Partial<WizardSummaryData>[] = [];
    // Process upload ID
    idUploadProcess: number | null = null;
    showWizard = false;
    files: File[] = [];
    uploadConfig!: PcvizUploadConfig;
    workflowUploadConfig!: PcvizUploadConfig;
    layerNoticeSummary = '';
    pathError: string | undefined;

    // poincloud conversion
    pointcloudConversion: PointcloudConversionOptions = {
        config: 1,
    };
    // uav conversion
    uavConversion: UavImportOptions = {
        skipImages: true,
    };
    // gis conversion
    gisConversion: GisImporterConversionOptions = {
        geometryType: 'AUTODETECT',
        csvType: 'NEZ',
        ScaleFactor: 1,
    };
    layerName = '';
    layerDescription = '';
    jobSiteId: number | undefined;
    canEstimateCredits = false;
    fileData: PcvizCRP1FileData | undefined = undefined;

    private readonly filesLoadedSubject$ = new Subject<File[]>();
    readonly filesLoaded$ = this.filesLoadedSubject$.asObservable();

    private filterFormats = {
        ImportCtrlPts: ['UAVSimpleCsvReversed', 'UAVSimpleCsv'],
        ImportPositions: ['UAVBlock', 'UAVPhotoScan', 'UAVCsv', 'UAVSimpleCsv', 'UAVSimpleCsvReversed', 'UAVPix4D'],
        ImportStation: ['ScanProject', 'CRP1ScanProject'],
        ImportProject: ['ScanProject'],
    };

    private getFileContentsSubs: SubscriptionHandler | undefined;

    constructor(
        private reportService: ReportService,
        private authorizationService: AuthorizationService,
        private magnetService: MagnetService,
        private formBuilder: FormBuilder,
        private filesHelperService: FilesHelperService,
        private jobsInitializationService: JobsInitializationService,
        private formValidatorsService: CustomFormValidatorsService,
        private jobAdapterService: JobPcvizService,
        private uploadService: UploadService,
        private dbPcvizService: DbPcvizService,
        private selectionService: ObjectBrowserSelectionService,
        private workflowWizardService: WorkflowWizardService,
        private workspaceService: WorkspaceService,
        private datePipe: DatePipe,
        private translationService: TranslationService,
        private crp1ImportSdService: CRP1ImportSdService
    ) {}

    ngOnDestroy(): void {
        this.getFileContentsSubs?.cancel();
    }

    init(wizard?: TwcWizardComponent): void {
        if (wizard) {
            this.wizard = wizard;
        }

        this.jobsInitializationService.initJobAdapters(this.importType ?? 'project');
        this.getCompanyReport().subscribe((report) => (this.companyReport = report));

        this.wizardTitle = 'UPLOAD.TITLE';
        this.wizardSubtitle = '';
        this.files.length = 0;
        this.startedUpload = false;
        this.idUploadProcess = null;
        this.workflowWizardService.isWorkflowUploading = false;
        this.projectForm.reset();
        this.layerForm.reset();
        this.extraPanel.panelName = undefined;
        this.crsValue = { key: '', value: '' };
        this.formatSelection = undefined;
        this.summary = [];
        this.pointcloudConversion = {
            config: 1,
        };
        this.uavConversion = {
            skipImages: true,
        };
        this.fileData = undefined;
        this.gisConversion = {
            geometryType: 'AUTODETECT',
            csvType: 'NEZ',
            ScaleFactor: 1,
        };
        this.layerSelectionMode = 'none';
        this.uploadConfig = { files: [] };
        this.workflowUploadConfig = { files: [] };

        setTimeout(() => {
            this.summary = [];
            const wizardParams = this.wizard?.options.data as ImportWizardOptions;
            this.importType = wizardParams.importType;
            this.wizardTitle = wizardParams.title ?? this.wizardTitle;
            this.selectInitialJob = wizardParams.selectFirstJobAdapter || false;
            this.wizardParams = wizardParams;

            // This forces the reloading of components
            this.showWizard = true;

            this.jobsInitializationService.initJobAdapters(this.importType, this.selectInitialJob);
            this.initFormatDefinitions(this.selectInitialJob);

            const isWorkflowWizard = !!wizardParams.worklowTaskDefinition;
            if (isWorkflowWizard) {
                this.workflowUploadConfig = this.workflowWizardService.initJobData(this.importType, this.wizardTitle);
                this.wizardSubtitle = `SCANPROC.INFO_TASK_${this.importType}`;
            }

            this.initFormatDefinitions(this.selectInitialJob);
        }, 0);
        this.layerName = '';
        this.layerDescription = '';

        this.getFileContentsSubs = this.crp1ImportSdService.onGetFileContentsResponse().subscribe((response) => {
            this.handleFileGetContents(response);
            this.filesLoadedSubject$.next(this.files);
        });
    }

    getAvailableSize(): number {
        if (!this.companyReport) {
            return 0;
        }
        const licenceReport = this.companyReport.licences;
        const companyReport = this.companyReport.company;
        const storageAvailable = licenceReport.maxstorage - companyReport.storageSize;

        return storageAvailable;
    }

    getProjectName(): string | null {
        return this.projectForm.controls['projectName'].value;
    }

    getFormattedCaptureDate(form: FormGroup, formatToApply?: string): string | undefined {
        const captureDate = form.controls['projectCaptureDate'].value;
        const format = formatToApply ?? 'YYYY-MM-dd';
        const locale = this.translationService.currentLang;
        return this.datePipe.transform(captureDate, format, undefined, locale) || undefined;
    }

    isValidStep(): boolean {
        const inputDataStep = this.translationService.translate('WIZARD.TAB_FILES');
        const projectDataStep = this.translationService.translate('WIZARD.TAB_NEW_PROJECT');
        const layerDataStep = this.translationService.translate('WIZARD.TAB_OUTPUT_LAYER');
        const workspaceStep = this.translationService.translate('WIZARD.TAB_DESTINATION_GROUP');
        const addDataStep = this.translationService.translate('GENERAL.ADD');
        const addLayerOptions = this.translationService.translate('WIZARD.TAB_LAYER_OPTIONS');
        const selectCrp1ScansStep = this.translationService.translate('WIZARD.TAB_CRP1_SCAN_SELECTOR');

        if (!this.wizard?.selectedItem) {
            return false;
        }

        if (!this.thereIsEnoughSpace()) {
            return false;
        }

        let wizardValidity = true;

        switch (this.wizard.selectedItem.label) {
            case inputDataStep:
            case addDataStep:
                wizardValidity = this.isValidStepInputData();
                break;
            case layerDataStep:
            case addLayerOptions:
                wizardValidity = this.isValidStepLayer();
                break;
            case projectDataStep:
                wizardValidity = this.isValidStepProject();
                break;
            case workspaceStep:
                wizardValidity = this.isWorkspaceValid;
                break;
            case selectCrp1ScansStep: {
                const jobAdapter = this.jobAdapterService.getJobAdapter() as PcvizCRP1ScanProjectJobAdapter;
                wizardValidity = !!jobAdapter.getScans()?.length;
                break;
            }
            default:
                wizardValidity = true;
        }

        return wizardValidity;
    }

    areFilesInvalid(): boolean {
        let result = true;
        const currentJobAdapter = this.jobAdapterService.getJobAdapter();

        if (currentJobAdapter && this.files.length) {
            result = !currentJobAdapter?.areLayerFilesValid(this.files);
        }

        return result;
    }

    initStepWizard(index: number): void {
        if (index === 0) {
            this.initStepWizard0();
        }
    }

    /**
     * upload the files to the server to process them
     */
    uploadAndProcess(jobSiteId?: number): void {
        this.startedUpload = true;

        this.setUploadConfigInfo();

        switch (this.importType) {
            case 'project':
                this.uploadAndProcessProject(jobSiteId);
                break;
            case 'layer':
                this.uploadAndProcessLayer();
                break;
            case 'CreateStation':
            case 'ImportProject':
            case 'UpgradeSparse':
                this.uploadConfig.uploadId = this.workflowUploadConfig.uploadId;
                this.workflowWizardService.submitProcess(this.uploadConfig);
                break;
            case 'ImportRefs':
            case 'ImportStation':
                this.uploadConfig.uploadId = this.workflowUploadConfig.uploadId;
                this.uploadConfig.workspace = {
                    name: this.workspaceService.workspaceForm.get('newDatasetName')?.value,
                };
                this.workflowWizardService.submitProcess(this.uploadConfig);
                break;
            default:
                break;
        }
    }

    pauseUpload(): void {
        if (typeof this.uploadConfig.upload?.pause === 'function') {
            this.uploadConfig.upload.pause();
        }
    }

    resumeUpload(): void {
        if (typeof this.uploadConfig.upload?.resume === 'function') {
            this.uploadConfig.upload.resume();
        }
    }

    cancelUpload(): void {
        if (typeof this.uploadConfig.upload?.cancel === 'function') {
            this.uploadConfig.upload.cancel();
        }

        this.addErrorToSummary('UPLOAD.CANCELLED_BY_USER');
    }

    uploadMoreData(): void {
        do {
            this.wizard.prevStep();
        } while (!this.wizard.isFirstStep);

        this.init();
    }

    getExtension(file: File): string {
        return this.filesHelperService.getExtension(file);
    }

    isSite(): boolean {
        return this.magnetService.isSite();
    }

    isLayer(): boolean {
        return this.importType === 'layer';
    }

    isCreateStation(): boolean {
        return this.importType === 'CreateStation';
    }

    isImportReferences(): boolean {
        return this.importType === 'ImportRefs';
    }

    isPointCloud(): boolean {
        return this.jobAdapterService.getJobAdapter()?.getLayerType() === 'PointCloud';
    }

    isImageLayer(): boolean {
        return this.jobAdapterService.getJobAdapter()?.getId() === 'VLU';
    }

    isModel3DLayer(): boolean {
        return this.jobAdapterService.getJobAdapter()?._formatId === 'Model3DVectorLayer';
    }

    isFeatureLayer(): boolean {
        const jobAdapter: PcvizJobAdapter | undefined = this.jobAdapterService.getJobAdapter();

        if (!jobAdapter) {
            return false;
        }

        return jobAdapter.getLayerType() === 'VectorLayer' && jobAdapter.getId() !== 'M3DVLU' && jobAdapter.getId() !== 'VLU';
    }

    isRaster(): boolean {
        return this.jobAdapterService.getJobAdapter()?.getLayerType() === 'RasterLayer';
    }

    isProject(): boolean {
        return this.importType === 'project';
    }

    isImportJobSiteReferenceData(): boolean {
        return this.importType === 'ImportJobSiteRefs';
    }

    /** Flag to store if the credits can't be estimated */
    onCreditsCanBeEstimated(e: boolean): void {
        this.canEstimateCredits = this.isSite() ? false : e;
    }

    /**
     * Returns true if the step label is the previous step than the current one
     * This is done so we can get a way to see when the step has changed to do some work
     * @param wizard The TWC Wizard
     * @param stepLabel step label to check
     */
    comparePreviousStep(wizard: TwcWizardComponent, stepLabel: string): boolean {
        let result = false;
        const currentStep = wizard.selectedIndex;
        if (currentStep > 0) {
            const prevItem = wizard.wizardSteps?.items?.toArray()[currentStep - 1];
            result = prevItem?.label === stepLabel;
        }

        return result;
    }

    async createAndUploadJobSiteReferenceData(): Promise<PcvizUploadConfig> {
        this.startedUpload = true;
        this.jobAdapterService.getJobAdapter()?.setObjectType('layers');
        this.setUploadConfigInfo();

        this.uploadConfig.object = {
            name: this.layerForm.get('name')?.value,
        };

        try {
            await this.uploadService.createAndUploadJobSiteReferenceData(this.uploadConfig);
        } catch {
            if (this.uploadConfig.upload) {
                this.uploadConfig.upload = {
                    filesInfo: [],
                };
            }
            this.startedUpload = false;
            throw new Error();
        }

        return this.uploadConfig;
    }

    openExternalDialog(): void {
        this.crp1ImportSdService.openFileDialog();
    }

    get isImportStation(): boolean {
        return this.workflowWizardService.isImportStation();
    }

    get isCrp1(): boolean {
        return this.formatSelection?.id === this.jobAdapterService.getCrp1ScanProjectJobAdapter().CRP1_IMPORT_ID;
    }

    private handleFileGetContents(response: Event): void {
        const message = response.message;
        if (message.success === false && !message.error) {
            this.files = [];
            this.pathError = undefined;
            this.fileData = undefined;
            return;
        }

        if (message.success === false && message.error === 'INVALID_DATA_PATH') {
            const sdcardPathLabel = this.translationService.translate('UPLOAD.SDCARD_PATH');
            this.pathError = `${sdcardPathLabel}: ${message.path}`;
            this.fileData = undefined;
            return;
        }

        this.files = [
            {
                name: message?.path,
                size: parseInt(message?.size),
            },
        ] as File[];
        const jobAdapter = this.jobAdapterService.getJobAdapter() as PcvizCRP1ScanProjectJobAdapter;
        jobAdapter.setDataPath(message.path);
        this.fileData = message;
        this.pathError = undefined;
    }

    private getCompanyReport(): Observable<Report> {
        return this.reportService.getByScopeAndUserId('company', this.authorizationService.getUserLogged().userId);
    }

    private uploadAndProcessLayer(): void {
        const selectedLayer = this.selectionService.getSelectedElements('layers')?.[0];
        const hasAssociatedProject = this.layerForm.get('hasProject')?.value ?? false;

        this.uploadConfig.object = (selectedLayer as any) ?? {
            name: this.layerForm.get('name')?.value,
            description: this.layerForm.get('description')?.value ?? '',
        };

        if (hasAssociatedProject) {
            this.uploadConfig.project = {
                project_data: {
                    name: this.layerForm.get('projectName')?.value ?? '',
                    description: this.layerForm.get('projectDescription')?.value ?? '',
                    captureDate: this.getFormattedCaptureDate(this.layerForm),
                },
            };
        }

        this.uploadService.uploadAndProcessLayer(this.uploadConfig);
    }

    private uploadAndProcessProject(jobSiteId?: number): void {
        this.uploadConfig.object = {
            name: this.projectForm.get('projectName')?.value,
            captureDate: this.getFormattedCaptureDate(this.projectForm),
        };

        this.uploadService.uploadAndProcessProject(this.uploadConfig, this.summary, jobSiteId).then(() => {
            if (jobSiteId && this.uploadConfig?.object?.id) {
                const onRefresh$ = this.wizard?.options?.data?.onRefresh$;
                onRefresh$?.next({
                    projectId: this.uploadConfig?.object?.id,
                    projectType: this.uploadConfig?.object?.projectType,
                    openViewer: true,
                });
            }
        });
    }

    private addErrorToSummary(err: string): void {
        const firtsSummary = this.summary.find((item) => !!item);

        if (firtsSummary) {
            firtsSummary.errors = firtsSummary.errors ?? [];
            firtsSummary.errors.push(err);
        }
    }

    private initStepWizard0(): void {
        if (this.isProject()) {
            this.setDefaultProjectProperties();
        } else {
            this.setDefaultLayerProperties();
        }
    }

    private setDefaultProjectProperties(): void {
        const projectNameField = this.projectForm.controls['projectName'];
        const defaultName: string = this.jobAdapterService.getJobAdapter()?._projectName ?? '';

        this.projectForm.patchValue({
            projectName: defaultName,
            projectCaptureDate: this.today,
        });
        this.nameProjectMaxLength = null;

        if (this.isSite() && this.projectType === ProjectType.SCAN) {
            this.nameProjectMaxLength = 63;
            projectNameField.addValidators(this.regexpProjectNameSiteVal);
        }
    }

    private setDefaultLayerProperties(): void {
        const name: string = this.filesHelperService.getFileNameWithoutExtension(this.files[0]);

        this.layerForm.controls['name'].setValue(name);
        this.layerForm.controls['projectCaptureDate'].setValue(this.today);
    }

    private setUploadConfigInfo(): void {
        this.uploadConfig = {
            files: this.files,
            formatType: { type: this.formatSelection?.id },
            jobAdapter: this.jobAdapterService.getJobAdapter(),
            upload: {
                status: 'SELECTED',
                filesInfo: [{ name: this.files[0].name, size: this.files[0].size }],
            },
            uploadId: this.dbPcvizService.getUtils().generateUUID(),
            object: {
                name: this.layerName,
                description: this.layerDescription,
            },
        };
    }

    /**
     * Get the format definitions that will be enabled with the current wizard import selection (project or layer)
     */
    private initFormatDefinitions(selectInitialJob: boolean): void {
        let tempFormatDefinitions: any[] = [];

        const uploaders = this.jobAdapterService.getJobAdapterRegistryInstance().importers;
        uploaders.forEach((uploader: any) => {
            if (!uploader.isEnabled()) {
                return;
            }

            if (uploader.getFormatDefinitions()) {
                tempFormatDefinitions = tempFormatDefinitions.concat(uploader.getFormatDefinitions());
            }
        });

        this.formatsDefinition.next(this.validateFormats(tempFormatDefinitions));

        if (selectInitialJob) {
            this.formatSelection = tempFormatDefinitions[0];
        }
    }

    private validateFormats(tempFormatDefinitions: WizardFormatDefinition[]): WizardFormatDefinition[] {
        return tempFormatDefinitions
            .filter((format) => this.validateFormat(format.id))
            .map((format) => {
                const label = this.hasFilter() ? `${format.label}_${this.workflowWizardService.task.name}` : format.label;
                return { ...format, label };
            });
    }

    private validateFormat(format: WizardFormatDefinition['id']): boolean {
        if (!this.hasFilter()) {
            return true;
        }
        const validFormats = this.filterFormats[this.workflowWizardService.task.name as keyof typeof this.filterFormats];
        if (!validFormats) {
            return true;
        }
        const isValidFormat = validFormats.some((validFormat) => format === validFormat);
        return !!isValidFormat;
    }

    private hasFilter(): boolean {
        if (!this.workflowWizardService.task) {
            return false;
        }
        const keys = Object.keys(this.filterFormats);
        const isFilterFormat = keys.some((key) => key === this.workflowWizardService.task.name);

        return !!isFilterFormat;
    }

    private thereIsEnoughSpace(): boolean {
        const serverSpace: number = this.getAvailableSize();
        let filesSize = 0;

        for (const file of this.files) {
            filesSize += file.size;
        }

        return serverSpace - filesSize > 0;
    }

    private isValidStepInputData(): boolean {
        const currentJobAdapter = this.jobAdapterService.getJobAdapter();
        const arefilesValid: boolean = currentJobAdapter?.areLayerFilesValid(this.files) ?? false;

        return !!this.formatSelection && arefilesValid;
    }

    private isValidStepLayer(): boolean {
        let isStepValid = false;

        if (this.layerSelectionMode === 'new') {
            isStepValid = this.layerForm.valid;
        }

        if (this.layerSelectionMode === 'existing') {
            isStepValid = this.selectionService.getNumberSelectedElements() > 0;
        }

        return isStepValid;
    }

    private isValidStepProject(): boolean {
        return this.projectForm.valid;
    }
}
